
import Vue from "vue";
import { adminLoginWithIdToken, persistAdminApiJwt } from "@/api";
import amplifyConfig from "@/config/amplifyConfig";
import { persistAdminOAuthFailure } from "@/auth/adminOAuthError";

function safeRead(storage: Storage | undefined, key: string) {
  if (!storage) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch (error) {
    console.warn("AuthCallback.vue: Failed to read storage", { key, error });
    return null;
  }
}

export default Vue.extend({
  name: "AuthCallback",
  data() {
    return {
      debugInfo: "",
      hubUnsubscribe: null as any,
      authCheckInterval: null as any,
      processed: false,
      retryCount: 0,
      maxAuthRetries: 5, // Max attempts to fetch session
      authPollingInterval: 1500, // 1.5 seconds
      authCode: "",
      manualAttempted: false,
      codeRedeemed: false
    };
  },
  async mounted() {
    console.log(
      "AuthCallback.vue mounted: Starting OAuth callback processing..."
    );
    console.log("AuthCallback.vue: Current URL:", window.location.href);
    console.log("AuthCallback.vue: URL params:", window.location.search);

    this.debugInfo = "Processing OAuth callback...";

    // Extract URL parameters BEFORE any Amplify imports
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get("code");
    const state = urlParams.get("state");
    const error = urlParams.get("error");

    console.log("AuthCallback.vue: URL parameters:", {
      authCode,
      state,
      error
    });

    if (error) {
      console.error("AuthCallback.vue: OAuth error received:", error);
      this.debugInfo = `OAuth error: ${error}`;
      setTimeout(() => this.$router.push(`/login?error=${error}`), 2000);
      return;
    }

    if (!authCode) {
      console.error("AuthCallback.vue: No auth code found in URL");
      this.debugInfo = "No authorization code found";
      setTimeout(() => this.$router.push("/login?error=no_code"), 2000);
      return;
    }

    this.authCode = authCode;

    // DO NOT configure Amplify on callback page - it will consume the code
    // Skip Hub listener setup - we don't need Amplify events here

    // Immediately attempt manual token exchange
    this.debugInfo = "Exchanging authorization code for tokens...";
    await this.handleOAuthCallback();
  },

  beforeDestroy() {
    if (this.hubUnsubscribe) {
      this.hubUnsubscribe();
    }
    this.stopAuthPolling();
  },

  methods: {
    stopAuthPolling() {
      if (this.authCheckInterval) {
        clearInterval(this.authCheckInterval);
        this.authCheckInterval = null;
      }
    },

    getHostedUiCache() {
      if (typeof window === "undefined") {
        return null;
      }

      try {
        const raw = window.sessionStorage.getItem(
          "amplify-signin-with-hostedUI"
        );

        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        console.warn(
          "AuthCallback.vue: Failed to parse hosted UI cache",
          error
        );
        return null;
      }
    },

    decodeJwt(token: string) {
      if (typeof window === "undefined") {
        return null;
      }

      try {
        const payload = token.split(".")[1];
        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
        const json = window.atob(normalized);
        return JSON.parse(json);
      } catch (error) {
        console.warn("AuthCallback.vue: Failed to decode JWT", error);
        return null;
      }
    },

    async setupHubListener() {
      console.log(
        "AuthCallback.vue: Setting up Hub listener for auth events..."
      );

      const { Hub } = await import("aws-amplify/utils");

      const hubListener = (data: any) => {
        console.log("AuthCallback.vue: Hub event received:", data);

        const { channel, payload } = data;

        if (channel === "auth") {
          console.log("AuthCallback.vue: Auth event:", payload.event, payload);

          switch (payload.event) {
            case "signInWithRedirect":
              console.log(
                "AuthCallback.vue: signInWithRedirect event received"
              );
              this.debugInfo =
                "OAuth redirect completed, processing authentication...";
              // Give a bit more time for tokens to be processed
              this.checkAuthenticationWithDelay(2000);
              break;

            case "signInWithRedirect_failure":
              console.error(
                "AuthCallback.vue: signInWithRedirect failed:",
                payload.data
              );
              this.debugInfo = "OAuth authentication failed";
              setTimeout(
                () => this.$router.push("/login?error=oauth_failure"),
                2000
              );
              break;

            case "signedIn":
              console.log("AuthCallback.vue: User signed in event");
              this.debugInfo = "User signed in successfully!";
              this.handleSuccessfulAuth();
              break;

            case "tokenRefresh":
              console.log("AuthCallback.vue: Token refresh event");
              this.checkAuthenticationWithDelay(1000);
              break;

            case "customOAuthState":
              console.log(
                "AuthCallback.vue: Custom OAuth state event:",
                payload.data
              );
              break;

            default:
              console.log("AuthCallback.vue: Other auth event:", payload.event);
          }
        }
      };

      this.hubUnsubscribe = Hub.listen("auth", hubListener);
    },

    async handleOAuthCallback() {
      console.log("AuthCallback.vue: Handling OAuth callback...");
      this.debugInfo = "Processing OAuth callback...";

      // Immediately try manual token exchange (no Amplify, no delays)
      const tokens = await this.tryManualTokenExchange();

      if (tokens) {
        console.log("AuthCallback.vue: ✅ Manual token exchange succeeded!");
        await this.handleSuccessfulAuth(
          { tokens, manual: true },
          { skipSessionFetch: true, manual: true }
        );
        return;
      }

      // If manual exchange failed, show error
      console.error("AuthCallback.vue: Manual token exchange failed");
      this.debugInfo = "Authentication failed. Redirecting to login...";
      setTimeout(
        () => this.$router.push("/login?error=token_exchange_failed"),
        2000
      );
    },

    checkAuthenticationWithDelay(delay: number) {
      setTimeout(() => {
        if (!this.processed) {
          this.checkAuthenticationStatus();
        }
      }, delay);
    },

    async checkAuthenticationStatus() {
      if (this.processed) {
        console.log("AuthCallback.vue: Already processed, skipping check");
        return;
      }

      // This method is now unused - handleOAuthCallback does everything
      console.warn(
        "AuthCallback.vue: checkAuthenticationStatus called but should not be used"
      );
    },

    async handleSuccessfulAuth(
      establishedSession?: any,
      options?: { skipSessionFetch?: boolean; manual?: boolean }
    ) {
      if (this.processed) {
        console.log("AuthCallback.vue: Authentication already processed");
        return;
      }

      this.processed = true;
      this.stopAuthPolling();

      try {
        console.log("AuthCallback.vue: Handling successful authentication...");
        this.debugInfo = "Authentication successful! Getting user info...";

        let session = establishedSession;

        // Skip session fetch entirely - we're using manual tokens only
        if (!session && !options?.skipSessionFetch) {
          console.log(
            "AuthCallback.vue: No session provided, using manual tokens"
          );
        }

        // Ensure we have an ID token to send to the backend
        const tokensContainer = session?.tokens || session;
        let idTokenValue = tokensContainer?.idToken?.toString?.() ?? null;
        let accessTokenValue =
          tokensContainer?.accessToken?.toString?.() ?? null;

        if (!idTokenValue || !accessTokenValue) {
          const hostedCache = this.getHostedUiCache();
          if (hostedCache?.tokens) {
            const { tokens } = hostedCache;
            idTokenValue =
              idTokenValue || tokens.idToken || tokens.id_token || null;
            accessTokenValue =
              accessTokenValue ||
              tokens.accessToken ||
              tokens.access_token ||
              null;
          }
        }

        if (!idTokenValue && session?.manual) {
          idTokenValue = session?.tokens?.id_token || session?.tokens?.idToken;
          accessTokenValue =
            session?.tokens?.access_token || session?.tokens?.accessToken;
        }

        if (!idTokenValue) {
          console.error(
            "AuthCallback.vue: No tokens available after OAuth redirect."
          );
          this.debugInfo = "Authentication data missing. Redirecting to login…";
          setTimeout(() => this.$router.push("/login?error=no_tokens"), 2000);
          return;
        }

        // Skip getCurrentUser - we don't need Amplify session info
        // We'll decode the ID token directly for user info
        let currentUser: any = null;

        console.log("AuthCallback.vue: Final session:", session);

        const oauthLogin = await adminLoginWithIdToken(idTokenValue);

        if (!oauthLogin?.token || !oauthLogin?.admin) {
          console.error(
            "AuthCallback.vue: Backend did not return a valid admin token"
          );
          this.debugInfo =
            "Unable to establish admin session. Redirecting to login...";
          setTimeout(() => this.$router.push("/login?error=admin_token"), 2000);
          return;
        }

        const tokenPayload = this.decodeJwt(idTokenValue);

        const email =
          oauthLogin.admin.email ||
          oauthLogin.admin.username ||
          currentUser?.signInDetails?.loginId ||
          currentUser?.username ||
          tokenPayload?.email ||
          tokenPayload?.preferred_username ||
          tokenPayload?.["cognito:username"] ||
          "";

        const userId =
          oauthLogin.admin.id?.toString() ||
          currentUser?.userId ||
          tokenPayload?.sub ||
          "";

        persistAdminApiJwt(oauthLogin.token);
        localStorage.setItem("user", JSON.stringify(oauthLogin.admin));
        localStorage.setItem("adminId", userId || "");
        localStorage.setItem("cognitoIdToken", idTokenValue);
        if (accessTokenValue) {
          localStorage.setItem("cognitoAccessToken", accessTokenValue);
        }

        this.clearHostedUiCache();
        this.clearAmplifyOAuthArtifacts();

        console.log("AuthCallback.vue: Stored auth data in localStorage");

        // Notify App.vue to update auth state
        this.$root.$emit("forceAuthCheck");

        // Clear URL to prevent accidental reloads with stale code
        if (typeof window !== "undefined" && window.history) {
          window.history.replaceState({}, document.title, "/");
        }

        this.debugInfo = "Authentication complete! Redirecting to dashboard...";
        setTimeout(() => {
          this.$router.push("/");
        }, 1000);
      } catch (error) {
        console.error(
          "AuthCallback.vue: Error during successful auth handling:",
          error instanceof Error ? error.message : error
        );
        this.debugInfo =
          "Authentication processing failed. Redirecting to login...";
        persistAdminOAuthFailure(error);
        setTimeout(
          () => this.$router.push("/login?error=auth_processing"),
          2000
        );
      }
    },

    clearHostedUiCache() {
      if (typeof window === "undefined") {
        return;
      }

      try {
        window.sessionStorage.removeItem("amplify-signin-with-hostedUI");
      } catch (error) {
        console.warn(
          "AuthCallback.vue: Failed to clear hosted UI cache",
          error
        );
      }
    },

    async clearAmplifyOAuthArtifacts() {
      const keys = this.getOAuthStorageKeys();
      if (!keys) {
        return;
      }

      const { clearOAuthArtifacts } = await import("@/auth/client");
      clearOAuthArtifacts(keys.clientId);
    },

    getOAuthConfig() {
      const authConfig = (amplifyConfig?.Auth as any)?.Cognito;
      if (!authConfig?.userPoolClientId) {
        return null;
      }

      return {
        clientId: authConfig.userPoolClientId,
        domain: authConfig.loginWith?.oauth?.domain,
        redirectSignIn: authConfig.loginWith?.oauth?.redirectSignIn,
        redirectSignOut: authConfig.loginWith?.oauth?.redirectSignOut,
        responseType: authConfig.loginWith?.oauth?.responseType ?? "code"
      };
    },

    getOAuthStorageKeys() {
      const config = this.getOAuthConfig();
      if (!config?.clientId) {
        return null;
      }

      const prefix = "CognitoIdentityServiceProvider";
      const clientId = config.clientId;
      return {
        clientId,
        inflight: `${prefix}.${clientId}.inflightOAuth`,
        pkce: `${prefix}.${clientId}.oauthPKCE`,
        state: `${prefix}.${clientId}.oauthState`,
        signIn: `${prefix}.${clientId}.oauthSignIn`,
        fallbackPkce: "tempstars.oauth.pkce",
        fallbackState: "tempstars.oauth.state"
      };
    },

    getRedirectUri() {
      const config = this.getOAuthConfig();
      if (!config) {
        return null;
      }

      const candidates = Array.isArray(config.redirectSignIn)
        ? config.redirectSignIn
        : [config.redirectSignIn];
      const currentOrigin =
        typeof window !== "undefined" ? window.location.origin : "";
      const match = candidates.find(target =>
        target?.startsWith("http") ? target.startsWith(currentOrigin) : false
      );
      if (match) {
        return match;
      }
      if (typeof window !== "undefined") {
        return `${currentOrigin}/auth/callback`;
      }

      return candidates[0] || null;
    },

    async tryManualTokenExchange() {
      if (this.codeRedeemed) {
        console.warn(
          "AuthCallback.vue: Code already redeemed, skipping manual exchange"
        );
        return null;
      }

      if (!this.authCode) {
        return null;
      }

      const config = this.getOAuthConfig();
      if (!config?.clientId || !config?.domain) {
        console.warn(
          "AuthCallback.vue: Missing OAuth configuration, cannot perform manual token exchange."
        );
        return null;
      }

      const { readOAuthArtifact } = await import("@/auth/client");
      const keys = this.getOAuthStorageKeys();
      let codeVerifier: string | null = null;
      if (keys) {
        codeVerifier =
          readOAuthArtifact(keys.pkce) || readOAuthArtifact(keys.fallbackPkce);
        console.log(
          "AuthCallback.vue: Retrieved PKCE verifier for manual exchange:",
          codeVerifier ? "present" : "missing"
        );
      }

      if (!codeVerifier) {
        return null;
      }

      // Validate state parameter to prevent CSRF attacks
      const urlParams = new URLSearchParams(window.location.search);
      const urlState = urlParams.get("state");
      const storedState = keys
        ? readOAuthArtifact(keys.state) || readOAuthArtifact(keys.fallbackState)
        : null;

      if (!storedState || storedState !== urlState) {
        console.error(
          "AuthCallback.vue: State mismatch - possible CSRF attack",
          {
            urlState: urlState?.substring(0, 10) + "...",
            storedState: storedState?.substring(0, 10) + "..."
          }
        );
        this.manualAttempted = true;
        return null;
      }

      console.log("AuthCallback.vue: State validation passed");

      // Mark code as being redeemed to prevent duplicate attempts
      this.manualAttempted = true;
      this.codeRedeemed = true;

      try {
        const redirectUri = this.getRedirectUri();
        const tokenUrl = `https://${config.domain}/oauth2/token`;
        const body = new URLSearchParams({
          grant_type: "authorization_code",
          client_id: config.clientId,
          code: this.authCode,
          redirect_uri: redirectUri || "",
          code_verifier: codeVerifier
        });

        console.log("AuthCallback.vue: Attempting manual token exchange", {
          url: tokenUrl,
          clientId: config.clientId,
          redirectUri,
          codeLength: this.authCode.length,
          verifierLength: codeVerifier.length,
          verifierFirst6: codeVerifier.substring(0, 6),
          verifierLast6: codeVerifier.substring(codeVerifier.length - 6)
        });

        const response = await fetch(tokenUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body: body.toString()
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "AuthCallback.vue: Manual token exchange failed",
            response.status,
            errorText
          );
          return null;
        }

        const tokenResponse = await response.json();
        console.log(
          "AuthCallback.vue: Manual token exchange response received",
          {
            hasIdToken: !!tokenResponse.id_token,
            hasAccessToken: !!tokenResponse.access_token
          }
        );

        if (!tokenResponse.id_token) {
          return null;
        }

        return {
          idToken: tokenResponse.id_token,
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token
        };
      } catch (error) {
        console.error(
          "AuthCallback.vue: Manual token exchange threw an error",
          error
        );
        return null;
      }
    },

    logAmplifyStorage() {
      if (typeof window === "undefined") {
        return;
      }

      const keys = this.getOAuthStorageKeys();
      const snapshot: Record<string, string | null> = {};

      if (keys) {
        Object.entries(keys).forEach(([label, key]) => {
          if (label === "clientId") {
            return;
          }

          const localValue = safeRead(window.localStorage, key);
          const sessionValue = safeRead(window.sessionStorage, key);
          snapshot[`${label}.local`] = localValue;
          snapshot[`${label}.session`] = sessionValue;
        });
      }

      console.log(
        "AuthCallback.vue: Amplify OAuth storage snapshot:",
        snapshot
      );
    }
  }
});
